import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import API from "../services/api";
import "./AdminVendorList.css";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "-");
const orDash = (v) => (v === 0 ? "0" : v == null || String(v).trim() === "" ? "-" : String(v));
const phoneLink = (p) => (p ? <a href={`tel:${p}`}>{p}</a> : "");
const mailLink = (m) => (m ? <a href={`mailto:${m}`}>{m}</a> : "");
const getInitials = (name = "") =>
  String(name || "")
    .split(" ")
    .map((part) => part.trim().charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "AS";
const getAvatarTone = (seed = "") => {
  const tones = ["blue", "green", "amber", "violet", "rose", "sky"];
  const total = String(seed || "").split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return tones[total % tones.length];
};

const getSupplierId = (s) => String(s?._id || "");
const getCompany = (s) => s.companyName || s.supplier?.companyName || "";
const getSupplierName = (s) => s.supplierName || s.supplier?.supplierName || s.fullName || "";
const getGST = (s) => s.gstNumber || s.supplier?.gstNumber || "";
const getAddress = (s) => s.address || s.supplier?.address || "";
const getContact = (s) => s.contactPerson || s.supplier?.contactPerson || s.fullName || "";
const getMobile = (s) => s.contactMobile || s.supplier?.contactMobile || s.mobile || "";
const getEmail = (s) => s.email || "";
const getCode = (s) => s.supplier?.supplierCode || "";

const exportCSV = (rows) => {
  if (!rows?.length) return;
  const headers = ["Supplier", "Company", "Email", "Mobile", "GST", "Address", "CreatedAt", "RawID"];
  const csv = [
    headers.join(","),
    ...rows.map((s) =>
      [
        getSupplierName(s),
        getCompany(s),
        getEmail(s),
        getMobile(s),
        getGST(s),
        getAddress(s),
        s.createdAt ? new Date(s.createdAt).toISOString() : "",
        getSupplierId(s),
      ]
        .map((x) => `"${String(x ?? "").replaceAll('"', '""')}"`)
        .join(",")
    ),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `assigned_suppliers_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const AssignedSuppliers = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [date, setDate] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchSuppliers = async () => {
    setLoading(true);
    setError("");
    try {
      let url = "/employee/my-suppliers?full=1";
      if (date) url += `&date=${date}`;
      const res = await API.get(url);
      setSuppliers(Array.isArray(res.data) ? res.data : res.data?.items || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load suppliers. Please try again.");
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const stats = useMemo(() => {
    const total = suppliers.length;
    const approved = suppliers.filter((s) => s.approved === true).length;
    const locked = suppliers.filter((s) => s.supplier?.profileLocked).length;
    return { total, approved, locked };
  }, [suppliers]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter((s) =>
      [
        getSupplierName(s),
        getCompany(s),
        getEmail(s),
        getMobile(s),
        getGST(s),
        getAddress(s),
        getCode(s),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [suppliers, q]);

  return (
    <div className="vm-page">
      <div className="vm-container">
        <div className="vm-topbar">
          <div className="vm-title-wrap">
            <div className="vm-logo">AS</div>
            <div>
              <div className="vm-title">Assigned Suppliers</div>
              <div className="vm-subtitle vm-subtitle-normal">Supplier profiles assigned to you by admin</div>
            </div>
          </div>
          <div className="vm-search vm-search-top d-none d-md-block">
            <span className="material-symbols-outlined">search</span>
            <input
              type="search"
              placeholder="Search suppliers by name, company, GST, mobile..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="vm-actions">
            <button className="vm-toolbar-btn vm-toolbar-btn-primary" onClick={() => exportCSV(filtered)} disabled={!filtered.length}>
              <span className="material-symbols-outlined">download</span>
              <span>Export CSV</span>
            </button>
            <button className="vm-toolbar-btn" onClick={fetchSuppliers}>
              <span className="material-symbols-outlined">refresh</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        <div className="vm-toolbar">
          <div className="vm-tabs">
            <button className="vm-tab active" type="button">
              <span>Total Assigned</span>
              <span className="vm-tab-badge blue">{stats.total}</span>
            </button>
            <button className="vm-tab" type="button">
              <span>Approved</span>
              <span className="vm-tab-badge green">{stats.approved}</span>
            </button>
            <button className="vm-tab" type="button">
              <span>Locked Profiles</span>
              <span className="vm-tab-badge amber">{stats.locked}</span>
            </button>
          </div>
          <div className="vm-filter-row">
            <div className="vm-filter-stack">
              <div className="d-flex gap-2 flex-wrap">
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="form-control"
                  style={{ width: 170, borderRadius: 10, fontSize: 12 }}
                />
                <Button size="sm" variant="outline-primary" onClick={() => setDate(new Date().toISOString().split("T")[0])}>
                  Today
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setDate("")}>
                  Clear
                </Button>
              </div>
              <div className="vm-search d-md-none">
                <span className="material-symbols-outlined">search</span>
                <input type="search" placeholder="Search suppliers..." value={q} onChange={(e) => setQ(e.target.value)} />
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center my-5">
            <Spinner animation="border" />
            <div className="mt-2 text-muted">Loading suppliers...</div>
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && filtered.length > 0 ? (
          <div className="vm-content">
            <Row xs={1} md={2} xl={3} className="g-3">
              {filtered.map((s, i) => {
                const supplierId = getSupplierId(s);
                const company = getCompany(s);
                const supplierName = getSupplierName(s);
                const contact = getContact(s);
                const email = getEmail(s);
                const mobile = getMobile(s);
                const gst = getGST(s);
                const address = getAddress(s);
                const code = getCode(s);
                const profileLocked = !!s.supplier?.profileLocked;

                return (
                  <Col key={supplierId || i}>
                    <div className="vm-entity-grid-card">
                      <div className="vm-entity-head">
                        <div className="vm-supplier-head">
                          <div className={`vm-avatar vm-avatar-${getAvatarTone(supplierId || company || supplierName)}`}>
                            {getInitials(company || supplierName)}
                          </div>
                          <div className="vm-supplier-copy">
                            <div className="vm-person-name">{orDash(company || supplierName)}</div>
                            <div className="vm-company-name">{orDash(contact)}</div>
                            <div className="vm-code-chip">{orDash(code || "Supplier")}</div>
                          </div>
                        </div>
                        <div className="vm-status-stack">
                          <span className={`vm-status-pill ${s.approved ? "approved" : "pending"}`}>
                            {s.approved ? "Approved" : "Pending"}
                          </span>
                          <span className={`vm-mini-pill ${profileLocked ? "waiting" : "neutral"}`}>
                            Edit: {profileLocked ? "locked" : "open"}
                          </span>
                        </div>
                      </div>
                      <div className="vm-kv-grid">
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">smartphone</span>Mobile</span>
                          <span className="vm-v">{phoneLink(mobile) || "-"}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">mail</span>Email</span>
                          <span className="vm-v">{mailLink(email) || "-"}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">badge</span>GST Number</span>
                          <span className="vm-v vm-v-mono">{orDash(gst)}</span>
                        </div>
                        <div className="vm-kv">
                          <span className="vm-k"><span className="material-symbols-outlined">person</span>Supplier Name</span>
                          <span className="vm-v">{orDash(supplierName)}</span>
                        </div>
                        <div className="vm-kv vm-kv-full">
                          <span className="vm-k"><span className="material-symbols-outlined">location_on</span>Address</span>
                          <span className="vm-v">{orDash(address)}</span>
                        </div>
                      </div>
                      <div className="vm-meta-row">
                        <div className="vm-meta-chip">
                          <span className="material-symbols-outlined">calendar_today</span>
                          <span>Created: {fmtDate(s.createdAt)}</span>
                        </div>
                        <div className="vm-meta-chip">
                          <span className="material-symbols-outlined">update</span>
                          <span>Updated: {fmtDate(s.updatedAt)}</span>
                        </div>
                      </div>
                      <div className="vm-actions-wrap">
                        <Button size="sm" className="vm-action-btn vm-action-neutral" onClick={() => navigate(`/employee/my-suppliers/${supplierId}`)}>
                          <span className="material-symbols-outlined">badge</span>
                          <span>Full Profile</span>
                        </Button>
                        <Button
                          size="sm"
                          className="vm-action-btn vm-action-neutral"
                          onClick={() => setSelected({ company, supplierName, contact, email, mobile, gst, address, profileLocked })}
                        >
                          <span className="material-symbols-outlined">visibility</span>
                          <span>Quick View</span>
                        </Button>
                        {!profileLocked ? (
                          <Badge bg="success" className="d-inline-flex align-items-center justify-content-center rounded-pill">
                            Edit Enabled
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        ) : (
          !loading && !error && <p className="text-muted">No suppliers found{q ? " for this search." : date ? " for this date." : "."}</p>
        )}

        <Modal show={!!selected} onHide={() => setSelected(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Supplier Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selected && (
              <div className="px-1">
                <div className="mb-2"><strong>Company:</strong> {selected.company || ""}</div>
                <div className="mb-2"><strong>Supplier:</strong> {selected.supplierName || ""}</div>
                <div className="mb-2"><strong>Contact:</strong> {selected.contact || ""}</div>
                <div className="mb-2"><strong>Email:</strong> {selected.email || ""}</div>
                <div className="mb-2"><strong>Mobile:</strong> {selected.mobile || ""}</div>
                <div className="mb-2"><strong>GST:</strong> {selected.gst || ""}</div>
                <div className="mb-2"><strong>Address:</strong> {selected.address || ""}</div>
                <div className="mb-2"><strong>Edit Lock:</strong> {selected.profileLocked ? "Locked" : "Edit Enabled"}</div>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default AssignedSuppliers;
